import { useCallback, useEffect, useMemo, useState } from "react";
import { FinanceData, Transaction } from "./types";
import { FinanceRepository, LocalStorageRepository } from "./storage";

export type NewTransaction = Omit<Transaction, "id">;

const defaultRepository = new LocalStorageRepository();

// Single entry point for reading and changing finance data
export function useFinance(repository: FinanceRepository = defaultRepository) {
    const [data, setData] = useState<FinanceData>(() => repository.load());

    useEffect(() => {
        repository.save(data);
    }, [data, repository]);

    const addTransaction = useCallback((transaction: NewTransaction) => {
        const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setData(d => ({ ...d, transactions: d.transactions.concat({ ...transaction, id }) }));
    }, []);

    const deleteTransaction = useCallback((id: string) => {
        setData(d => ({ ...d, transactions: d.transactions.filter(t => t.id !== id) }));
    }, []);

    const setBudget = useCallback((categoryId: string, limit: number | null) => {
        setData(d => {
            const others = d.budgets.filter(b => b.categoryId !== categoryId);
            return { ...d, budgets: limit === null ? others : others.concat({ categoryId, limit }) };
        });
    }, []);

    const reset = useCallback(() => setData(repository.reset()), [repository]);

    const categoriesById = useMemo(() => {
        const map: Record<string, FinanceData["categories"][number]> = {};
        data.categories.forEach(c => {
            map[c.id] = c;
        });
        return map;
    }, [data.categories]);

    return { data, categoriesById, addTransaction, deleteTransaction, setBudget, reset };
}
